/**
 * Per-column comparison-type override — the "the catalog's type is a placeholder, let the user
 * choose" case (PVAL-26423).
 *
 * Flex Positions / Flex Transactions return every AGGREGATE_INFO column as `TEXT` regardless of what
 * its values actually look like, because the blob is untyped in storage. The chosen type is
 * therefore UI-owned state that has to be sent back on every use of the column (a filter's
 * `filterType`, a sort's `type`, and the field's presence under `context.fieldStorage.aggInfo`).
 *
 * The vocabulary is deliberately narrower than the full filter-type union: only the three
 * comparisons Postgres can be asked to apply to an untyped JSON value are offered.
 */
export type ColumnTypeOverride = 'text' | 'number' | 'date'

/** The default when the user has expressed no preference — matches the catalog's own `TEXT`. */
export const DEFAULT_COLUMN_TYPE_OVERRIDE: ColumnTypeOverride = 'text'

export interface ColumnTypeOverrideOption {
  value: ColumnTypeOverride
  label: string
}

/** Picker options, in display order. */
export const COLUMN_TYPE_OVERRIDE_OPTIONS: readonly ColumnTypeOverrideOption[] =
  [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
  ]

export const isColumnTypeOverride = (
  value: unknown,
): value is ColumnTypeOverride =>
  value === 'text' || value === 'number' || value === 'date'

/**
 * Icon glyph per type, matching the Match Rules aggInfo picker this is styled after. Shared between
 * {@link ColumnTypePickerComponent} (the picker itself) and any column-header badge that needs to
 * show the CURRENT choice without opening the picker — one map, so the two surfaces cannot drift to
 * different symbols for the same type.
 */
export const COLUMN_TYPE_OVERRIDE_ICON: Record<ColumnTypeOverride, string> = {
  text: 'A',
  number: '#',
  date: 'mdi mdi-calendar-blank',
}

/** Icon colour per type — matching the reference design's colour-coded glyphs. */
export const COLUMN_TYPE_OVERRIDE_ICON_COLOR_CLASS: Record<
  ColumnTypeOverride,
  string
> = {
  text: 'hs-column-type-icon-text',
  number: 'hs-column-type-icon-number',
  date: 'hs-column-type-icon-date',
}

/** Whether a type's icon is an mdi icon-font class (Date) rather than a literal glyph (Text/Number). */
export const isColumnTypeIconClass = (icon: string): boolean =>
  icon.startsWith('mdi')

/**
 * A whole scope's overrides, as persisted with a saved view / column layout. Keyed by the column's
 * canonical `field`.
 */
export type ColumnTypeOverrideMap = Record<string, ColumnTypeOverride>
